import React, { useState, useEffect, useMemo } from 'react';

/**
 * Props:
 *   open (bool)
 *   proposal { success: [], conflicts: [], hasAdjustments: bool }
 *   availableLeaveTypes  — the target-side leave types (for reassign dropdowns)
 *   onCancel()
 *   onConfirm(adjustmentActions)
 *
 * Adjustment action shape:
 *   { adjustmentId, action: 'keep' | 'drop' | 'reassign', targetLeaveTypeId? }
 *
 * Auto-mapped adjustments (from proposal.success) are pre-built as 'reassign'
 * actions and submitted alongside any conflict resolutions the admin picks.
 */
const RemapAdjustmentsModal = ({
  open,
  proposal,
  availableLeaveTypes = [],
  onCancel,
  onConfirm
}) => {
  const [conflictActions, setConflictActions] = useState({});

  useEffect(() => {
    if (!proposal) return;
    // default every conflict to 'drop' so submission is always valid
    const defaults = {};
    (proposal.conflicts || []).forEach(c => {
      defaults[c.adjustmentId] = { action: 'drop' };
    });
    setConflictActions(defaults);
  }, [proposal]);

  const finalActions = useMemo(() => {
    if (!proposal) return [];
    const autoReassigns = (proposal.success || [])
      .filter(s => s.action === 'remap')
      .map(s => ({
        adjustmentId: s.adjustmentId,
        action: 'reassign',
        targetLeaveTypeId: s.newLeaveTypeId
      }));
    const conflictResolutions = Object.entries(conflictActions).map(([adjustmentId, v]) => ({
      adjustmentId,
      action: v.action,
      targetLeaveTypeId: v.targetLeaveTypeId
    }));
    return [...autoReassigns, ...conflictResolutions];
  }, [proposal, conflictActions]);

  if (!open || !proposal) return null;

  const successAutoRemap = (proposal.success || []).filter(s => s.action === 'remap');
  const conflicts = proposal.conflicts || [];

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-[60] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Review Leave Adjustments</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            This user has existing leave adjustments. Confirm what happens to them when the new policy is assigned.
          </p>
        </div>
        <div className="p-6 space-y-4">
          {successAutoRemap.length > 0 && (
            <div className="bg-green-50 dark:bg-green-900/20 rounded p-3">
              <p className="text-sm font-medium text-green-800 dark:text-green-300 mb-1">
                {successAutoRemap.length} adjustment{successAutoRemap.length === 1 ? '' : 's'} will be kept
              </p>
              <ul className="text-xs text-green-700 dark:text-green-200 space-y-1">
                {successAutoRemap.map(s => (
                  <li key={s.adjustmentId}>
                    {s.leaveTypeName}: {s.adjustmentDays >= 0 ? '+' : ''}{s.adjustmentDays} days — remapped to new policy type
                  </li>
                ))}
              </ul>
            </div>
          )}

          {conflicts.length > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded p-3">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-2">
                {conflicts.length} adjustment{conflicts.length === 1 ? '' : 's'} can&apos;t be auto-matched
              </p>
              <div className="space-y-3">
                {conflicts.map(c => (
                  <div key={c.adjustmentId} className="bg-white dark:bg-gray-800 p-3 rounded border border-amber-200 dark:border-amber-700">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {c.leaveTypeName || '(unknown type)'}: {c.adjustmentDays >= 0 ? '+' : ''}{c.adjustmentDays} days
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">{c.reason}</div>
                    <div className="flex items-center space-x-2 text-sm">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={conflictActions[c.adjustmentId]?.action === 'drop'}
                          onChange={() => setConflictActions(a => ({ ...a, [c.adjustmentId]: { action: 'drop' } }))}
                          className="text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-1">Drop</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={conflictActions[c.adjustmentId]?.action === 'keep'}
                          onChange={() => setConflictActions(a => ({ ...a, [c.adjustmentId]: { action: 'keep' } }))}
                          className="text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-1">Keep (orphan)</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          checked={conflictActions[c.adjustmentId]?.action === 'reassign'}
                          onChange={() => setConflictActions(a => ({ ...a, [c.adjustmentId]: { action: 'reassign', targetLeaveTypeId: availableLeaveTypes[0]?.id } }))}
                          className="text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-1">Reassign to:</span>
                      </label>
                      <select
                        value={conflictActions[c.adjustmentId]?.targetLeaveTypeId || ''}
                        disabled={conflictActions[c.adjustmentId]?.action !== 'reassign'}
                        onChange={e => setConflictActions(a => ({ ...a, [c.adjustmentId]: { action: 'reassign', targetLeaveTypeId: e.target.value } }))}
                        className="text-sm rounded border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-40"
                      >
                        <option value="">—</option>
                        {availableLeaveTypes.map(lt => (
                          <option key={lt.id} value={lt.id}>{lt.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {successAutoRemap.length === 0 && conflicts.length === 0 && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              No adjustments need remapping for this change.
            </p>
          )}
        </div>
        <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700 flex justify-end space-x-2">
          <button onClick={onCancel} className="py-2 px-4 border border-gray-300 dark:border-gray-500 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={() => onConfirm(finalActions)} className="py-2 px-4 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700">
            Confirm & Assign
          </button>
        </div>
      </div>
    </div>
  );
};

export default RemapAdjustmentsModal;
